/**
 * The Carts scope — stage vocabulary, expiry threshold, how the screen's sorting and filters map
 * onto the cart API, and the permissions that gate the screen. The record shape both scopes share
 * lives in `models/cart-order.models`; the API's own shapes in `cart-api.models`.
 */

import type {
  CartStage,
  DateFieldList,
  ExportFormat,
  LabeledOption,
  StatusMeta,
} from '../../models/cart-order.models';
import type {
  CartApiDateField,
  CartApiSortField,
  CartExportFormat,
  CartListStage,
  CartListType,
  CartRole,
  CartStatusFilterValue,
} from './cart-api.models';

export const CART_STAGES: readonly StatusMeta<CartStage>[] = [
  { value: 'draft', label: 'Draft', cls: 'st-draft' },
  { value: 'approval', label: 'Approval Pending', cls: 'st-approval' },
  { value: 'approved', label: 'Approved', cls: 'st-approved' },
  { value: 'rejected', label: 'Rejected', cls: 'st-rejected' },
  { value: 'sent', label: 'Sent', cls: 'st-sent' },
  { value: 'viewed', label: 'Viewed', cls: 'st-viewed' },
  { value: 'signed', label: 'Signed', cls: 'st-signed' },
  { value: 'expired', label: 'Expired', cls: 'st-expired' },
  { value: 'voided', label: 'Voided', cls: 'st-voided' },
];

/** A cart lapsing inside this many days earns the red urgency marker beside its contact. */
export const URGENT_DAYS = 7;

/** The Saved Carts / Saved Proposals toggle (US 18.1). Saved Carts is where the list opens. */
export const CART_STAGE_OPTIONS: readonly LabeledOption<CartListStage>[] = [
  { value: 'contract', label: 'Saved Carts' },
  { value: 'proposal', label: 'Saved Proposals' },
];

// ── Sorting ──────────────────────────────────────────────────────
// The API sorts, so only the columns it can sort on are sortable. Events is not.

export type CartSortColumn =
  | 'status'
  | 'doc'
  | 'company'
  | 'contact'
  | 'id'
  | 'rep'
  | 'total'
  | 'balance'
  | 'sent'
  | 'expires'
  | 'touched';

/** Most recent activity first — what a rep wants to pick back up, and the API's own default. */
export const CART_DEFAULT_SORT: CartSortColumn = 'touched';

/** The `sort_by` each column sends. Balance Due equals the Order Total until signing. */
export const CART_SORT_PARAM: Readonly<Record<CartSortColumn, CartApiSortField>> = {
  status: 'status',
  doc: 'cart_type',
  company: 'company_name',
  contact: 'client_name',
  id: 'cart_number',
  rep: 'created_by_name',
  total: 'total',
  balance: 'total',
  sent: 'sent_at',
  expires: 'expiration_date',
  touched: 'updated_at',
};

// ── Filters ──────────────────────────────────────────────────────

/** The dates the API can filter on. It has no sent-date filter, so Sent is not offered. */
export type CartDateField = 'created' | 'touched' | 'expires';

export const CART_DATE_FIELDS: DateFieldList<CartDateField> = [
  { value: 'created', label: 'Created' },
  { value: 'touched', label: 'Last activity' },
  { value: 'expires', label: 'Expiration' },
];

export const CART_DATE_PARAM: Readonly<Record<CartDateField, CartApiDateField>> = {
  created: 'created_at',
  touched: 'updated_at',
  expires: 'expiration_date',
};

/*
 * The option lists below are what the filters show until `GET /carts/filter-options` answers, and
 * if it never does. The API's lists replace them — its labels and order win.
 */

/** Signed is missing on purpose: a signed cart is an order and never lists on Carts. */
export const CART_STATUS_OPTIONS: readonly LabeledOption<CartStatusFilterValue>[] = [
  { value: 'draft', label: 'Draft' },
  // The API has one status for a shared cart; Viewed is told apart only from its details, so this
  // option covers both Sent and Viewed carts (US 18.9).
  { value: 'shared_not_signed', label: 'Sent' },
  { value: 'approval_pending', label: 'Approval Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'expired', label: 'Expired' },
  { value: 'voided', label: 'Voided' },
  { value: 'expiring', label: 'Expiring' },
  { value: 'expiring_7_days', label: `Expiring in ${URGENT_DAYS} days` },
];

/** US 18.3. Nothing ticked means All Types. */
export const CART_TYPE_OPTIONS: readonly LabeledOption<CartListType>[] = [
  { value: 'contract', label: 'Contract' },
  { value: 'proposal', label: 'Proposal' },
  { value: 'add_on', label: 'Add-on' },
  { value: 'onsite', label: 'Onsite' },
  { value: 'self_service', label: 'Self Service' },
];

export const CART_ROLE_OPTIONS: readonly LabeledOption<CartRole>[] = [
  { value: 'sales', label: 'Sales' },
  { value: 'booth_builds', label: 'Booth Build' },
  { value: 'client_services', label: 'Client Services' },
  { value: 'self_service', label: 'Self Service' },
];

// ── Export (US 18.22) ────────────────────────────────────────────

/** What `GET /carts/export` produces. No Google Sheet: the API does not make one. */
export const CART_EXPORT_FORMATS: readonly (ExportFormat & { ext: CartExportFormat })[] = [
  { ext: 'pdf', label: 'PDF', note: 'Print-ready snapshot of the list', icon: 'pdf' },
  { ext: 'xlsx', label: 'Excel', note: 'Formatted workbook with column headers', icon: 'excel' },
  { ext: 'csv', label: 'CSV', note: 'Plain comma-separated values', icon: 'csv' },
];

/** The API refuses a larger view outright, so the screen says so before asking. */
export const CART_EXPORT_MAX_ROWS = 5000;

// ── Permissions ──────────────────────────────────────────────────
// The per-cart actions are not here: the API returns them on each row (`available_actions`),
// already resolved from these permissions and the cart's state. These gate the screen and its
// list-level buttons.

/** The `cart` module's permission keys, as the API's permission list names them. */
export const CART_PERMISSIONS = {
  LIST: 'cart.list',
  READ: 'cart.read',
  CREATE: 'cart.create',
  UPDATE: 'cart.update',
  CUSTOM_PRICE: 'cart.custom-price',
  EDIT_FEES: 'cart.edit-fees',
  ASSIGN_SALES_REP: 'cart.assign-sales-rep',
  MANAGE_PAYMENT_PLAN: 'cart.manage-payment-plan',
  SHARE: 'cart.share',
  VOID: 'cart.void',
  REACTIVATE: 'cart.reactivate',
  BYPASS_SIGNATURE: 'cart.bypass-signature',
  IMPERSONATE: 'cart.impersonate',
  SEND_RESET_PASSWORD: 'cart.send-reset-password', // NOSONAR — a permission slug, not a credential.
  INVITE_USER: 'cart.invite-user',
  MANAGE_PAYMENT_TERMS: 'cart.manage-payment-terms',
  BYPASS_PAYMENT: 'cart.bypass-payment',
  EDIT_LIST_PRICE: 'cart.edit-list-price',
  MANAGE_PORTAL_USERS: 'cart.manage-portal-users',
  EXPORT: 'cart.export',
  ONSITE_WAIVE_SERVICE_CHARGE: 'cart.onsite-waive-service-charge',
  EDIT_EXPIRATION: 'cart.edit-expiration',
  REQUEST_APPROVAL: 'cart.request-approval',
  CLONE: 'cart.clone',
  DELETE: 'cart.delete',
  ONSITE_PAY_AND_SIGN: 'cart.onsite-pay-and-sign',
  APPROVE: 'cart.approve',
} as const;

export type CartPermission = (typeof CART_PERMISSIONS)[keyof typeof CART_PERMISSIONS];
